import { useState } from 'react';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import Button from '../../../components/base/Button';

const NorthDakotaRequirementsPage = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      question: 'Areas of Examination Focus',
      answer: 'During examinations, North Dakota regulators assess the quality and completeness of your cybersecurity program, including policy documentation, risk assessment methodology, employee training records, vendor oversight practices, incident response capabilities, and technical controls. Examiners look for evidence of continuous improvement, timely remediation of identified deficiencies, and alignment with GLBA and state consumer protection requirements.'
    },
    {
      question: 'Documentation Standards',
      answer: 'Institutions must maintain comprehensive documentation of all cybersecurity activities, including written policies and procedures, risk assessment reports, training completion records, vendor due diligence reviews, incident response logs, and evidence of board or management oversight. Documentation should demonstrate a systematic approach to information security and be readily available for regulatory review.'
    },
    {
      question: 'Continuous Improvement Expectations',
      answer: 'Regulators expect financial institutions to demonstrate ongoing enhancement of their cybersecurity posture through regular policy updates, incorporation of lessons learned from incidents or near-misses, adoption of emerging security technologies, and proactive response to evolving threat landscapes. Annual reviews and updates to security programs are considered best practice.'
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-amber-50 via-white to-gray-50 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <i className="ri-government-line"></i>
                North Dakota Compliance
              </div>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                North Dakota <span className="text-amber-600">Cybersecurity Requirements</span>
              </h1>
              <p className="text-xl text-gray-600 mb-4">
                Financial Services Firms, Mortgage Lenders, Brokers, Servicers, and Money Service Businesses
              </p>
              <p className="text-lg text-gray-600">
                North Dakota's regulatory framework requires financial institutions to implement comprehensive cybersecurity programs that protect customer information through written policies, regular risk assessments, employee training, vendor oversight, and robust data protection measures. These requirements align with state consumer-protection laws, the North Dakota Century Code (NDCC), and federal GLBA standards to ensure the confidentiality, integrity, and availability of nonpublic personal information.
              </p>
            </div>
          </div>
        </section>

        {/* Disclaimer */}
        <section className="py-8 bg-yellow-50 border-y border-yellow-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-start gap-4">
                <i className="ri-information-line text-3xl text-yellow-600 flex-shrink-0"></i>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Important Disclaimer</h3>
                  <p className="text-gray-700 text-sm">
                    The information provided on this page is for general informational purposes only. North Dakota cybersecurity regulations are complex and subject to change. We recommend reaching out to our team to confirm you have covered all applicable requirements for your operations in North Dakota.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Required Cybersecurity Program */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="mb-12">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                    <i className="ri-shield-check-line text-2xl text-amber-600"></i>
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900">Required Cybersecurity Program</h2>
                </div>
                <p className="text-gray-700 mb-6">
                  Financial institutions licensed in North Dakota must maintain a comprehensive information security program that is appropriate to the size, complexity, and nature of their operations. This program serves as the foundation for protecting customer information and maintaining regulatory compliance.
                </p>
                
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-6 border-l-4 border-amber-600">
                    <h3 className="font-bold text-gray-900 mb-3">Core Program Components</h3>
                    <ul className="space-y-3 text-gray-700">
                      <li className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-line text-amber-600 flex-shrink-0 mt-1"></i>
                        <span><strong>Documented Cybersecurity Policies and Procedures:</strong> Written policies that clearly define security standards, responsibilities, and operational procedures for protecting customer information across all business functions</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-line text-amber-600 flex-shrink-0 mt-1"></i>
                        <span><strong>Designated Security Oversight Individual:</strong> A qualified person or team responsible for developing, implementing, and maintaining the information security program, with clear authority and accountability</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-line text-amber-600 flex-shrink-0 mt-1"></i>
                        <span><strong>Customer Information Protection Controls:</strong> Administrative, technical, and physical safeguards designed to protect customer information in accordance with North Dakota consumer-protection laws and regulatory expectations</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-line text-amber-600 flex-shrink-0 mt-1"></i>
                        <span><strong>GLBA and NDCC Alignment:</strong> Security controls that meet or exceed federal Gramm-Leach-Bliley Act requirements and North Dakota Century Code provisions for safeguarding nonpublic personal information</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Risk Assessment Requirements */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="mb-12">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                    <i className="ri-search-eye-line text-2xl text-amber-600"></i>
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900">Risk Assessment Requirements</h2>
                </div>
                <p className="text-gray-700 mb-6">
                  Regulated entities must perform periodic cybersecurity and IT risk assessments to identify, evaluate, and address threats to customer information. These assessments form the basis for prioritizing security investments and implementing appropriate controls.
                </p>
                
                <div className="space-y-4">
                  <div className="bg-white rounded-lg p-6 border-l-4 border-amber-600">
                    <h3 className="font-bold text-gray-900 mb-3">Assessment Scope and Requirements</h3>
                    <ul className="space-y-3 text-gray-700">
                      <li className="flex items-start gap-3">
                        <i className="ri-arrow-right-s-line text-amber-600 flex-shrink-0 mt-1"></i>
                        <span><strong>Threat Identification:</strong> Systematic identification of potential threats to customer information, including cyber attacks, insider threats, natural disasters, system failures, and human error</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-arrow-right-s-line text-amber-600 flex-shrink-0 mt-1"></i>
                        <span><strong>Vulnerability Evaluation:</strong> Comprehensive assessment of internal and external system vulnerabilities, including network architecture, application security, access controls, and third-party connections</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-arrow-right-s-line text-amber-600 flex-shrink-0 mt-1"></i>
                        <span><strong>Likelihood and Impact Assessment:</strong> Analysis of the probability that identified threats could exploit vulnerabilities and the potential business, operational, and customer impact of successful attacks</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-arrow-right-s-line text-amber-600 flex-shrink-0 mt-1"></i>
                        <span><strong>Documentation and Remediation:</strong> Detailed documentation of assessment findings, risk ratings, and specific remediation actions with timelines and responsible parties assigned</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-amber-50 rounded-lg p-6">
                    <div className="flex items-start gap-3">
                      <i className="ri-lightbulb-line text-2xl text-amber-600 flex-shrink-0"></i>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-2">Actionable Results Required</h4>
                        <p className="text-gray-700 text-sm">
                          Risk assessments must meaningfully influence the company's cybersecurity policies, control implementations, and resource allocations. Regulators expect to see clear connections between identified risks and the security measures implemented to address them.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Employee Training Requirements */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="mb-12">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                    <i className="ri-graduation-cap-line text-2xl text-amber-600"></i>
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900">Employee Training Requirements</h2>
                </div>
                <p className="text-gray-700 mb-6">
                  Institutions must implement ongoing security awareness training programs for all personnel to ensure employees understand their role in protecting customer information and can recognize and respond appropriately to security threats.
                </p>
                
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-6 border-l-4 border-amber-600">
                    <h3 className="font-bold text-gray-900 mb-3">Training Program Components</h3>
                    <ul className="space-y-3 text-gray-700">
                      <li className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-line text-amber-600 flex-shrink-0 mt-1"></i>
                        <span><strong>Customer Information Handling:</strong> Training on proper procedures for collecting, storing, transmitting, and disposing of customer information in compliance with privacy and security requirements</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-line text-amber-600 flex-shrink-0 mt-1"></i>
                        <span><strong>Threat Awareness:</strong> Education on recognizing and responding to phishing attempts, social engineering tactics, suspicious emails, and other common attack vectors targeting financial institutions</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-line text-amber-600 flex-shrink-0 mt-1"></i>
                        <span><strong>Access Control Responsibilities:</strong> Training on password security, multi-factor authentication, physical security, clean desk policies, and the importance of protecting authentication credentials</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-line text-amber-600 flex-shrink-0 mt-1"></i>
                        <span><strong>Role-Based Training:</strong> Specialized training for individuals with elevated access privileges, including IT staff, administrators, and personnel handling sensitive customer data</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-line text-amber-600 flex-shrink-0 mt-1"></i>
                        <span><strong>Annual Refreshers:</strong> Regular training updates to address emerging threats, policy changes, and lessons learned from security incidents, with documented participation records</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Access Control & Authentication */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="mb-12">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                    <i className="ri-lock-password-line text-2xl text-amber-600"></i>
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900">Access Control &amp; Authentication</h2>
                </div>
                <p className="text-gray-700 mb-6">
                  North Dakota regulators expect financial institutions to implement strong access management controls that ensure only authorized individuals can access customer information and critical systems, with appropriate authentication mechanisms in place.
                </p>
                
                <div className="space-y-4">
                  <div className="bg-white rounded-lg p-6 border-l-4 border-amber-600">
                    <h3 className="font-bold text-gray-900 mb-3">Access Control Standards</h3>
                    <ul className="space-y-3 text-gray-700">
                      <li className="flex items-start gap-3">
                        <i className="ri-arrow-right-s-line text-amber-600 flex-shrink-0 mt-1"></i>
                        <span><strong>Password Standards:</strong> Enforcement of strong password policies including minimum length, complexity requirements, regular expiration, and prohibition of password reuse across multiple accounts</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-arrow-right-s-line text-amber-600 flex-shrink-0 mt-1"></i>
                        <span><strong>Multi-Factor Authentication:</strong> Implementation of MFA for access to sensitive systems, customer information databases, remote access, and administrative functions to prevent unauthorized access</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-arrow-right-s-line text-amber-600 flex-shrink-0 mt-1"></i>
                        <span><strong>Least-Privilege Access Models:</strong> Granting users only the minimum access rights necessary to perform their job functions, with regular reviews to ensure access remains appropriate</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-arrow-right-s-line text-amber-600 flex-shrink-0 mt-1"></i>
                        <span><strong>Termination and Offboarding Procedures:</strong> Immediate revocation of system access, credentials, and physical access when employees leave the organization or change roles, with documented verification</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Vendor & Third-Party Oversight */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="mb-12">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                    <i className="ri-links-line text-2xl text-amber-600"></i>
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900">Vendor &amp; Third-Party Oversight</h2>
                </div>
                <p className="text-gray-700 mb-6">
                  Firms must evaluate and continuously monitor third-party service providers that have access to customer information or provide critical services. This oversight ensures that vendors maintain security standards consistent with the institution's own requirements.
                </p>
                
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-6 border-l-4 border-amber-600">
                    <h3 className="font-bold text-gray-900 mb-3">Third-Party Management Requirements</h3>
                    <ul className="space-y-3 text-gray-700">
                      <li className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-line text-amber-600 flex-shrink-0 mt-1"></i>
                        <span><strong>Due-Diligence Reviews:</strong> Comprehensive evaluation of potential vendors before engagement, including assessment of their cybersecurity capabilities, financial stability, business continuity plans, and regulatory compliance history</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-line text-amber-600 flex-shrink-0 mt-1"></i>
                        <span><strong>Contractual Security Expectations:</strong> Written agreements that clearly define security requirements, data protection obligations, audit rights, incident notification procedures, and liability provisions</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-line text-amber-600 flex-shrink-0 mt-1"></i>
                        <span><strong>Ongoing Monitoring:</strong> Regular assessment of vendor cybersecurity posture through security questionnaires, audit reports, penetration test results, and periodic reviews of their control environment</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-line text-amber-600 flex-shrink-0 mt-1"></i>
                        <span><strong>Incident-Notification Provisions:</strong> Requirements for vendors to promptly notify the institution of any security incidents, breaches, or events that could impact customer information or service availability</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Incident Response Requirements */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="mb-12">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                    <i className="ri-alarm-warning-line text-2xl text-amber-600"></i>
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900">Incident Response Requirements</h2>
                </div>
                <p className="text-gray-700 mb-6">
                  Institutions must maintain a documented incident response plan that enables rapid, coordinated response to cybersecurity events. The plan should address detection, containment, investigation, recovery, and regulatory reporting obligations.
                </p>
                
                <div className="space-y-4">
                  <div className="bg-white rounded-lg p-6 border-l-4 border-amber-600">
                    <h3 className="font-bold text-gray-900 mb-3">Incident Response Plan Components</h3>
                    <ul className="space-y-3 text-gray-700">
                      <li className="flex items-start gap-3">
                        <i className="ri-arrow-right-s-line text-amber-600 flex-shrink-0 mt-1"></i>
                        <span><strong>Roles and Responsibilities:</strong> Clear definition of incident response team members, their specific duties, decision-making authority, and escalation procedures for different types and severities of incidents</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-arrow-right-s-line text-amber-600 flex-shrink-0 mt-1"></i>
                        <span><strong>Containment and Investigation Procedures:</strong> Step-by-step processes for isolating affected systems, preserving evidence, determining the scope and cause of incidents, and preventing further damage</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-arrow-right-s-line text-amber-600 flex-shrink-0 mt-1"></i>
                        <span><strong>Communication Protocols:</strong> Guidelines for internal communication, customer notification, regulatory reporting, law enforcement coordination, and public relations management during and after incidents</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-arrow-right-s-line text-amber-600 flex-shrink-0 mt-1"></i>
                        <span><strong>Recordkeeping and Audit Trails:</strong> Requirements for documenting all incident response activities, decisions made, actions taken, and lessons learned to support regulatory examinations and continuous improvement</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-arrow-right-s-line text-amber-600 flex-shrink-0 mt-1"></i>
                        <span><strong>Regulatory Reporting Triggers:</strong> Clear criteria for determining when incidents must be reported to North Dakota regulators, particularly when customer data is exposed or operations are significantly disrupted</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Data Protection & Technical Controls */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="mb-12">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                    <i className="ri-shield-keyhole-line text-2xl text-amber-600"></i>
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900">Data Protection &amp; Technical Controls</h2>
                </div>
                <p className="text-gray-700 mb-6">
                  North Dakota regulators expect financial institutions to implement comprehensive technical safeguards that protect customer information throughout its lifecycle, from collection through secure disposal.
                </p>
                
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-6 border-l-4 border-amber-600">
                    <h3 className="font-bold text-gray-900 mb-3">Required Technical Safeguards</h3>
                    <ul className="space-y-3 text-gray-700">
                      <li className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-line text-amber-600 flex-shrink-0 mt-1"></i>
                        <span><strong>Encryption Requirements:</strong> Implementation of strong encryption for sensitive data at rest (stored on servers, databases, and portable devices) and in transit (transmitted over networks or the internet)</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-line text-amber-600 flex-shrink-0 mt-1"></i>
                        <span><strong>Secure System Configuration:</strong> Hardening of servers, workstations, and network devices by disabling unnecessary services, removing default accounts, and implementing security baselines</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-line text-amber-600 flex-shrink-0 mt-1"></i>
                        <span><strong>Logging and Monitoring:</strong> Comprehensive logging of system activities, security events, and access to customer information, with active monitoring to detect anomalies and potential security incidents</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-line text-amber-600 flex-shrink-0 mt-1"></i>
                        <span><strong>Patch Management:</strong> Timely application of security patches and updates to operating systems, applications, and firmware to address known vulnerabilities and reduce attack surface</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-line text-amber-600 flex-shrink-0 mt-1"></i>
                        <span><strong>Network Security:</strong> Implementation of firewalls, intrusion detection/prevention systems, network segmentation, and secure remote access solutions to protect against external and internal threats</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-line text-amber-600 flex-shrink-0 mt-1"></i>
                        <span><strong>Endpoint Protection:</strong> Deployment of anti-malware software, endpoint detection and response tools, and mobile device management solutions to protect workstations, laptops, and mobile devices</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-line text-amber-600 flex-shrink-0 mt-1"></i>
                        <span><strong>Physical Security:</strong> Controls to restrict physical access to offices, server rooms, and equipment containing customer information, including locks, access cards, visitor logs, and surveillance systems</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Examination Expectations Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-start gap-4 mb-8">
                <div className="w-16 h-16 flex items-center justify-center flex-shrink-0 bg-red-100 rounded-xl">
                  <i className="ri-file-search-line text-3xl text-red-600"></i>
                </div>
                <div>
                  <h2 className="text-4xl font-bold text-gray-900 mb-4">
                    Examination Expectations
                  </h2>
                  <p className="text-lg text-gray-600">
                    Financial institutions licensed in North Dakota are subject to periodic regulatory examinations that assess the effectiveness and completeness of their cybersecurity programs. These examinations evaluate compliance with GLBA requirements, state consumer protection laws, and general supervisory expectations for information security.
                  </p>
                </div>
              </div>

              <div className="mb-12">
                <div className="space-y-4">
                  {faqs.map((faq, index) => (
                    <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                      <button
                        onClick={() => toggleFaq(index)}
                        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                      >
                        <h3 className="text-xl font-bold text-gray-900">{faq.question}</h3>
                        <i className={`ri-arrow-down-s-line text-2xl text-gray-600 transition-transform duration-300 ${openFaq === index ? 'rotate-180' : ''}`}></i>
                      </button>
                      <div 
                        className={`overflow-hidden transition-all duration-300 ${openFaq === index ? 'max-h-96' : 'max-h-0'}`}
                      >
                        <div className="px-6 pb-4">
                          <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-red-50 border-l-4 border-red-600 p-6 rounded-lg">
                <div className="flex items-start gap-3">
                  <i className="ri-information-line text-red-600 text-xl mt-0.5 flex-shrink-0"></i>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Preparation is Key</h4>
                    <p className="text-gray-700">
                      Examiners expect to see evidence of a mature, well-documented cybersecurity program with clear accountability, regular testing, and continuous improvement. Institutions should maintain organized records and be prepared to demonstrate how their security controls address identified risks and comply with applicable regulations.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Closing Section */}
        <section className="py-16 bg-gradient-to-br from-amber-50 to-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Maintaining Compliance in North Dakota
                </h2>
                <p className="text-lg text-gray-700 mb-6">
                  Financial institutions, mortgage lenders, brokers, servicers, and money service businesses operating in North Dakota must maintain robust cybersecurity programs that protect customer information and demonstrate compliance with state and federal requirements. These programs require ongoing attention, regular updates, and continuous improvement to address evolving threats and regulatory expectations.
                </p>
                <p className="text-lg text-gray-700">
                  By implementing comprehensive security controls, conducting regular risk assessments, training employees, overseeing vendors, and maintaining effective incident response capabilities, institutions can protect their customers, maintain regulatory compliance, and build trust in their operations. North Dakota regulators expect financial firms to take these obligations seriously and demonstrate a commitment to information security through documented policies, effective controls, and continuous program enhancement.
                </p>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-8 border-t-4 border-amber-600">
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    Need Help with North Dakota Compliance?
                  </h3>
                  <p className="text-gray-700 mb-6">
                    Our team specializes in helping financial institutions meet North Dakota's cybersecurity requirements through comprehensive program development, risk assessments, policy documentation, and ongoing compliance support.
                  </p>
                  <div className="flex flex-wrap gap-4 justify-center">
                    <a href="/schedule-call" className="inline-block">
                      <button className="inline-flex items-center px-6 py-3 bg-amber-600 text-white font-semibold rounded-lg hover:bg-amber-700 transition-colors shadow-md whitespace-nowrap">
                        <i className="ri-calendar-line mr-2"></i>
                        Schedule Consultation
                      </button>
                    </a>
                    <a href="/risk-assessment" className="inline-block">
                      <button className="inline-flex items-center px-6 py-3 bg-white text-amber-600 font-semibold rounded-lg hover:bg-gray-50 transition-colors border-2 border-amber-600 whitespace-nowrap">
                        <i className="ri-shield-check-line mr-2"></i>
                        Get Risk Assessment
                      </button>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default NorthDakotaRequirementsPage;
